import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Menu } from '@tallerya/ui';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { Adjuntos } from '../../componentes/adjuntos/Adjuntos';
import { useTituloPantalla } from '../../layout/titulo';
import { usePermisos } from '../../permisos/usePermiso';
import { useAccionesGasto } from './AccionesGasto';
import { useFichaGasto, useMontosGastos, type FichaGasto as Ficha } from './datos';
import { useFormatoGasto } from './formato';
import { PanelHistorial } from './PanelHistorial';

// La ficha del gasto (#189): consultarlo sin abrir la ventana de editar. La ganó
// con el historial, por la regla de `estructura-del-proyecto.md` §7. No hay frame
// en el Figma —solo la fila de «Gastos de esta orden», `190:9042`—: sigue a
// `FichaProveedor.tsx`, y el Figma se ajusta después.
//
//   Órdenes › OS-0042 › Pastillas y líquido de frenos
//   Pastillas y líquido de frenos ⓘ  [Pendiente de pago]   [Historial] [Editar gasto] [•••]
//   Compra hoy · registró Carlos Méndez
//   Datos del gasto · Comprobante            (el historial, en un panel: #193)
//
// - **Pide `expense.create`**, como la API: es el permiso con que se ve el gasto
//   en la orden, y desde ahí se llega. Editar y lo del «•••» se ofrecen según el
//   permiso de cada uno (`useAccionesGasto`).
// - **El monto, solo con `expense.amount.view`**, leído de su propio endpoint.
// - **Las migas siguen al gasto, no al camino**: el de una orden cuelga de ella;
//   el del taller, del listado de la caja.
//
// Se llega desde el listado o desde la orden, que mandan `desde` en el estado:
// al eliminarlo se vuelve ahí con «Atrás». Quien entró por un enlace va a su orden,
// o al listado.

export function FichaGasto() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const consulta = useFichaGasto(id ?? null);
  useTituloPantalla(consulta.data?.description ?? t('admin.gastos.listado.titulo'));

  // Al eliminar desde aquí no se llega a ver «no existe»: `eliminar` no vuelve a
  // pedir la ficha eliminada, y se sigue viendo hasta que se sale.
  const ficha = consulta.data;

  if (consulta.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (!ficha) {
    const noExiste = consulta.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.gastos.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.gastos.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar(-1)}>
          {t('admin.gastos.volver')}
        </Boton>
      </section>
    );
  }

  return <Contenido ficha={ficha} />;
}

function Contenido({ ficha }: { ficha: Ficha }) {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { puede } = usePermisos();
  const { dia, cifra } = useFormatoGasto();
  const id = String(ficha.id);
  const orden = ficha.serviceOrderId ? String(ficha.serviceOrderId) : null;
  const folio = ficha.orderNumber ?? t('admin.ordenes.ficha.sinFolio');
  // El historial empieza cerrado y no se pide hasta abrirlo (#193).
  const [viendoHistorial, setViendoHistorial] = useState(false);

  const veMontos = puede('expense.amount.view');
  const montos = useMontosGastos([id], veMontos);
  const monto = montos.isError ? undefined : montos.data?.get(id);

  const desde = (useLocation().state as { desde?: 'listado' | 'orden' } | null)?.desde;
  // Eliminado, la ficha ya no existe: se vuelve a la pantalla de detrás, o se
  // reemplaza esta por la orden del gasto, o por el listado.
  const alEliminar = () =>
    void (desde ? navegar(-1) : navegar(orden ? `/ordenes/${orden}` : '/gastos', { replace: true }));

  const { acciones, editar, problema, enCurso, ventanas } = useAccionesGasto(
    { locationId: String(ficha.locationId), serviceOrderId: orden },
    alEliminar,
  );
  const masAcciones = acciones(ficha, monto, { conEditar: false });

  const resumen = [
    t('admin.gastos.ficha.compra', { fecha: dia(ficha.purchasedOn) }),
    ficha.createdByName && t('admin.gastos.registro', { persona: ficha.createdByName }),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <nav aria-label={t('admin.clientes.ficha.migas')}>
        <ol className="ficha__migas">
          {orden ? (
            <>
              <li>
                <Link to="/ordenes">{t('admin.ordenes.titulo')}</Link>
              </li>
              <li>
                <Link to={`/ordenes/${orden}`}>{folio}</Link>
              </li>
            </>
          ) : (
            <li>
              <Link to="/gastos">{t('admin.gastos.listado.titulo')}</Link>
            </li>
          )}
          <li aria-current="page">{ficha.description}</li>
        </ol>
      </nav>

      {problema && <AvisoError aviso={{ titulo: t('admin.gastos.noSePudo'), texto: problema }} />}

      <header className="tipos__cabecera">
        <div>
          <div className="tipos__titulo">
            <h1>{ficha.description}</h1>
            <Ayuda tema={ficha.description} texto={t('admin.gastos.ficha.info')} tono="titulo" />
            <span className={`orden__marca orden__marca--${ficha.paidOn ? 'neutro' : 'aviso'}`}>
              {ficha.paidOn ? t('admin.gastos.pagado', { fecha: dia(ficha.paidOn) }) : t('admin.gastos.pendiente')}
            </span>
          </div>
          <p className="tipos__resumen">{resumen}</p>
        </div>
        <div className="tipos__acciones">
          <Boton variante="secundario" onClick={() => setViendoHistorial(true)}>
            {t('admin.historial.titulo')}
          </Boton>
          {editar && (
            <Boton variante="secundario" onClick={() => editar(ficha)}>
              {t('admin.gastos.formulario.editar')}
            </Boton>
          )}
          {masAcciones.length > 0 && (
            <Menu
              elementos={masAcciones}
              disparador={({ alternar, atributos }) => (
                <Boton
                  variante="secundario"
                  soloIcono
                  disabled={enCurso === id}
                  aria-label={t('admin.gastos.mas')}
                  onClick={alternar}
                  {...atributos}
                >
                  •••
                </Boton>
              )}
            />
          )}
        </div>
      </header>

      <div className="ficha__secciones">
        <section className="tipos__seccion">
          <div className="tipos__seccion-cabecera">
            <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
              <h2>{t('admin.gastos.ficha.datos')}</h2>
              <Ayuda tema={t('admin.gastos.ficha.datos')} texto={t('admin.gastos.ficha.datosInfo')} tono="titulo" />
            </div>
          </div>
          <dl className="ficha__datos">
            {/* Mientras llega no se dibuja: un «Sin monto» que dura un instante confunde. */}
            {veMontos && monto && (
              <Dato etiqueta={t('admin.gastos.formulario.monto')}>
                {cifra(monto) ?? <span className="listado__aviso">{t('admin.gastos.sinMonto')}</span>}
              </Dato>
            )}
            <Dato etiqueta={t('admin.gastos.formulario.proveedor')}>
              {ficha.supplierName ?? (
                <span className="listado__aviso">{t('admin.gastos.formulario.sinProveedor')}</span>
              )}
            </Dato>
            <Dato etiqueta={t('admin.gastos.formulario.factura')}>{ficha.documentNumber ?? '—'}</Dato>
            <Dato etiqueta={t('admin.gastos.listado.columna.orden')}>
              {orden ? (
                <Link className="tipos__enlace" to={`/ordenes/${orden}`}>
                  {folio}
                </Link>
              ) : (
                t('admin.gastos.listado.delTaller')
              )}
            </Dato>
            <Dato etiqueta={t('admin.gastos.ficha.sucursal')}>{ficha.locationName ?? '—'}</Dato>
            {ficha.notes && <Dato etiqueta={t('admin.gastos.formulario.notas')}>{ficha.notes}</Dato>}
          </dl>
        </section>

        <section className="tipos__seccion">
          {/* Quitar una foto pide `expense.edit`; sin él, una «×» que la API rechaza.
              Subir pide menos, pero quien solo registra sube al registrar. */}
          <Adjuntos
            origen={{ tipo: 'gasto', id }}
            titulo={t('admin.gastos.comprobante.titulo')}
            info={t('admin.gastos.ficha.comprobanteInfo')}
            soloLectura={!puede('expense.edit')}
          />
        </section>

        {/* Quién le cambió qué, en el panel que abre «Historial», como en la ficha del cliente (#193). */}
        <PanelHistorial gastoId={id} abierto={viendoHistorial} onCerrar={() => setViendoHistorial(false)} />
      </div>

      {ventanas}
    </>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="ficha__dato">
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}
